import getAdjustedIconClassName from 'owa-mini-icons/lib/utils/getAdjustedIconClassName';
import React from 'react';
import { ColorScheme, getAccessibleTextColor } from 'mini-color-utils/lib/ColorScheme';
import { enterKeyHandler } from 'window-utils/lib/keyHandlerUtils';
import { HeaderIcon } from './HeaderIcon.types';
import { isMiniFeatureEnabled } from 'owa-mini-feature-flags';
import { mergeOverrideStyles } from 'mini-sass-mixins/lib/utils/mergeOverrideStyles';

const styles = require('./Header.scss');
const classNames = require('classnames/bind').bind(styles);

interface HeaderIconProps extends HeaderIcon {
    isRightIcon?: boolean;
    headerColorScheme?: ColorScheme;
    key?: string;
    isInArray?: boolean;
}

export const HeaderIconElement = React.forwardRef(
    (props: HeaderIconProps, ref?: React.RefObject<HTMLDivElement>) => {
        const {
            icon,
            onClick,
            onMouseDown,
            isDisabled,
            ariaLabel,
            ariaHasPopup,
            iconStyle,
            isPrimary,
            colorScheme: headerIconColorScheme,
            isBordered,
            isRightIcon = false,
            headerColorScheme,
            isInArray = false,
            wrapIconInContainer = true, // TODO: Refactor this to use a different parameter as discussed with davisj
        } = props;
        if (!icon && !wrapIconInContainer) {
            return null;
        }

        if (typeof icon === 'string') {
            const isTapFeedbackEnabled = isMiniFeatureEnabled('mini-tapFeedback');

            const isPrimaryBordered = isPrimary && isBordered;
            const containerStyles = {
                [styles.iconInContainer]: isInArray,
                [styles.leftIcon]: !isRightIcon && !isInArray,
                [styles.rightIcon]: isRightIcon && !isInArray,
                [styles.leftIconInContainer]: !isRightIcon && isInArray,
                [styles.rightIconInContainer]: isRightIcon && isInArray,
            };

            const content = (
                <i
                    ref={ref}
                    aria-hidden={!icon ? 'true' : 'false'}
                    role="button"
                    tabIndex={!icon ? -1 : 0}
                    aria-haspopup={!!ariaHasPopup}
                    aria-disabled={isDisabled}
                    className={classNames(
                        'header-button',
                        'ms-Icon',
                        { 'header-button--disabled': isDisabled },
                        styles.icon,
                        containerStyles,
                        iconStyle,
                        getAdjustedIconClassName(icon),
                        (headerIconColorScheme || headerColorScheme || isPrimary) && [
                            'header-button--primary',
                            isPrimaryBordered && styles.borderedPrimary,
                            isTapFeedbackEnabled &&
                                headerColorScheme &&
                                mergeOverrideStyles({
                                    selectors: {
                                        ':active': {
                                            background: headerColorScheme.schemeDark,
                                        },
                                    },
                                }),
                            isTapFeedbackEnabled &&
                                headerIconColorScheme &&
                                mergeOverrideStyles({
                                    selectors: {
                                        ':active': {
                                            background: headerIconColorScheme.schemeLighter,
                                        },
                                    },
                                    color: getAccessibleTextColor(headerIconColorScheme),
                                }),
                        ]
                    )}
                    onClick={onClick}
                    onMouseDown={onMouseDown}
                    onKeyPress={enterKeyHandler(onClick)}
                    title={ariaLabel ? ariaLabel : ''}
                    aria-label={ariaLabel ? ariaLabel : ''}
                />
            );

            return isPrimaryBordered ? (
                <div
                    className={classNames(
                        'header-button',
                        'header-button--primary',
                        styles.primaryBorderedButtonContainer,
                        containerStyles
                    )}>
                    {content}
                </div>
            ) : (
                content
            );
        } else {
            const wrapIcon: boolean = !!(iconStyle || isInArray);

            const clickProps =
                wrapIcon && onClick
                    ? {
                          onClick,
                          onKeyPress: enterKeyHandler(onClick),
                          role: 'button',
                          tabIndex: 0,
                          'aria-label': ariaLabel,
                          'aria-haspopup': ariaHasPopup,
                      }
                    : {};

            return wrapIcon ? (
                <div
                    {...clickProps}
                    className={classNames(
                        isInArray
                            ? isRightIcon
                                ? styles.rightIconInContainer
                                : styles.leftIconInContainer
                            : undefined,
                        iconStyle
                    )}>
                    {icon}
                </div>
            ) : (
                icon
            );
        }
    }
);
